import React from "react";

import cx from "classnames";

import { Card } from "../Card";
import { Icon, SIZES } from "../Icon";
import { IconButton } from "../IconButton";

import styles from "./Alert.scss";

/**
 * The text to display for the close/dismiss button. (This is where you pass your translations!)
 */
export type AlertText = { closeButtonMouseover: string };

export interface AlertProps {
  /**
   * The class name to apply to the container.
   */
  className?: string;
  /**
   * Changes the color and accessibility role of the alert.
   */
  variant?: "success" | "info" | "warning" | "default";
  /**
   * The callback to invoke when the close/dismiss button is clicked.
   */
  onDismiss?: (...args: any[]) => any;
  /**
   * The text to display for the close/dismiss button. (This is where you pass your translations!)
   */
  text?: AlertText;
}

/**
 * An alert component with a set of predefined variants to control color and
 * icon styles. Supports an optional `onDismiss` callback.
 */
export function Alert({
  className,
  children,
  variant = "default",
  onDismiss,
  text = { closeButtonMouseover: "" },
}: React.PropsWithChildren<AlertProps>) {
  const { color, icon } = getVariantProps(variant);
  const cardStyle = cx(styles.alert__container, className);
  return (
    <Card
      withBorder
      noShadow
      noPadding
      barLocation="top"
      barColor={color}
      className={cardStyle}
      data-testid="alert-card"
    >
      <Icon
        className={styles.alert__icon}
        icon={icon}
        color={color}
        size={SIZES.Large}
      />
      <div className={styles.alert__content}>{children}</div>
      {!!onDismiss && (
        /**
         * We need to hide interactive elements in toasts from assistive
         * technologies to avoid hijacking focus.
         * https://www.scottohara.me/blog/2019/07/08/a-toast-to-a11y-toasts.html
         * https://www.w3.org/WAI/WCAG21/Understanding/status-messages.html
         */
        <div className={styles.alert__dismiss} aria-hidden="true">
          <IconButton
            ariaLabel={text.closeButtonMouseover}
            icon="close"
            variant="transparent"
            onClick={onDismiss}
            data-testid="alert-dismiss-button"
          />
        </div>
      )}
    </Card>
  );
}

function getVariantProps(
  variant: "success" | "info" | "warning" | "default"
): Record<string, any> {
  switch (variant) {
    case "success":
      return { color: styles.systemGreen, icon: "checkmark", role: "status" };
    case "info":
      return { color: styles.systemBlue, icon: "info", role: "status" };
    case "warning":
      return { color: styles.systemRed, icon: "warning", role: "alert" };
    default:
      return { color: styles.asphalt, icon: "kazoo_logo", role: "status" };
  }
}
